import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import { Category_ } from 'src/api/db/apiCategory'
import {
  ChevronRight,
  ChevronDown,
  Edit,
  Trash2,
  ImageOff,
  ArrowRight,
} from 'lucide-react'
import { Button } from 'src/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/shared/components/ui/dropdown-menu'
import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { cn } from 'src/shared/components/ui/utils'
import { useSupabaseClient } from 'src/api/supabase'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from 'src/api/db/queryKeys'

interface CategoryNode {
  category: Category_
  children: CategoryNode[]
  productCount?: number
}

interface CategoryTreeProps {
  categories: Category_[]
  onEdit: (category: Category_) => void
  onDelete: (id: number) => void
  onMoveProducts: (category: Category_, productCount: number) => void
  onCategoryDrop?: (sourceCategoryId: number, targetCategory: Category_) => void
  searchQuery?: string
  draggedCategoryIdRef?: React.MutableRefObject<number | null>
}

const CategoryItem = ({
  node,
  level,
  onEdit,
  onDelete,
  onMoveProducts,
  onCategoryDrop,
  searchQuery,
  allCategories,
  draggedCategoryIdRef,
}: {
  node: CategoryNode
  level: number
  onEdit: (category: Category_) => void
  onDelete: (id: number) => void
  onMoveProducts: (category: Category_, productCount: number) => void
  onCategoryDrop?: (sourceCategoryId: number, targetCategory: Category_) => void
  searchQuery?: string
  allCategories?: Category_[]
  draggedCategoryIdRef?: React.MutableRefObject<number | null>
}) => {
  const { t } = useLocalization()
  const supabase = useSupabaseClient()
  const hasChildren = node.children.length > 0
  const category = node.category

  // Get product count for this category
  const { data: productCountData } = useQuery({
    queryKey: [queryKeys.categories, 'product-count', category.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('category_product')
        .select('*', { count: 'exact', head: true })
        .eq('category_id', category.id)

      if (error) throw error
      return { count: count || 0 }
    },
  })

  const productCount = productCountData?.count || 0

  // Get translated category title
  const getCategoryTitle = (cat: Category_) => {
    const parts = cat.title?.split(' || ') || []
    return t(
      parts[0] || 'Category',
      parts[1] || cat.title || `Category ${cat.id}`
    )
  }

  const translatedTitle = getCategoryTitle(category)

  // Highlight search matches (check both English and Swedish)
  const titleParts = category.title?.split(' || ') || []
  const englishTitle = titleParts[0] || category.title || ''
  const swedishTitle = titleParts[1] || ''
  const matchesSearch =
    !searchQuery ||
    englishTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    swedishTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    translatedTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())

  // Check if any children match search (recursively)
  const checkChildrenMatch = (children: CategoryNode[]): boolean => {
    return children.some((child) => {
      const childTitleParts = child.category.title?.split(' || ') || []
      const childEnglishTitle = childTitleParts[0] || child.category.title || ''
      const childSwedishTitle = childTitleParts[1] || ''
      const childTranslatedTitle = getCategoryTitle(child.category)
      const childMatches =
        !searchQuery ||
        childEnglishTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        childSwedishTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        childTranslatedTitle
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        child.category.description
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase())
      return childMatches || checkChildrenMatch(child.children)
    })
  }

  const hasMatchingChildren = hasChildren && checkChildrenMatch(node.children)

  // Show if matches or has matching children
  const shouldShow = matchesSearch || hasMatchingChildren

  // Auto-expand only if searching and has matching children
  const [isExpanded, setIsExpanded] = useState(
    !!searchQuery && hasMatchingChildren
  )

  // Auto-expand when search query changes and has matching children
  useEffect(() => {
    if (searchQuery && hasMatchingChildren) {
      setIsExpanded(true)
    } else if (!searchQuery) {
      setIsExpanded(false)
    }
  }, [searchQuery, hasMatchingChildren])

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)

  // Helper function to check if target category is a descendant of source category
  const isDescendant = useCallback((targetId: number, sourceId: number): boolean => {
    if (!allCategories) return false
    const categoryMap = new Map<number, Category_>()
    allCategories.forEach((cat) => categoryMap.set(cat.id, cat))
    
    const visited = new Set<number>()
    let currentCategory = categoryMap.get(targetId)
    
    while (currentCategory && !visited.has(currentCategory.id)) {
      visited.add(currentCategory.id)
      if (currentCategory.id === sourceId) {
        return true
      }
      if (currentCategory.parent_category_id) {
        currentCategory = categoryMap.get(currentCategory.parent_category_id)
      } else {
        break
      }
    }
    return false
  }, [allCategories])

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true)
    if (draggedCategoryIdRef) {
      draggedCategoryIdRef.current = category.id
    }
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('categoryId', category.id.toString())
    e.dataTransfer.setData('text/plain', category.id.toString())
  }

  const handleDragEnd = () => {
    setIsDragging(false)
    if (draggedCategoryIdRef) {
      draggedCategoryIdRef.current = null
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Get source category ID from ref (more reliable than dataTransfer during dragOver)
    const sourceCategoryId = draggedCategoryIdRef?.current
    
    // Don't allow dropping on self, invalid IDs, or if target is a descendant of source
    if (sourceCategoryId && sourceCategoryId !== category.id) {
      if (!isDescendant(category.id, sourceCategoryId)) {
        e.dataTransfer.dropEffect = 'move'
        setIsDragOver(true)
      } else {
        e.dataTransfer.dropEffect = 'none'
        setIsDragOver(false)
      }
    } else {
      setIsDragOver(false)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only clear drag over if we're actually leaving the element
    const relatedTarget = e.relatedTarget as HTMLElement
    if (!e.currentTarget.contains(relatedTarget)) {
      setIsDragOver(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)

    const sourceCategoryId = parseInt(e.dataTransfer.getData('categoryId') || '0', 10)
    
    // Validate drop: can't drop on self or if target is a descendant of source
    if (sourceCategoryId && sourceCategoryId !== category.id && onCategoryDrop) {
      if (!isDescendant(category.id, sourceCategoryId)) {
        onCategoryDrop(sourceCategoryId, category)
      }
    }
  }

  if (!shouldShow) {
    return null
  }

  return (
    <div className="select-none">
      <div
        draggable={!!onCategoryDrop}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'group flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors',
          level > 0 && 'ml-6',
          hasChildren && 'cursor-pointer',
          isDragging && 'opacity-50 cursor-grabbing',
          isDragOver && 'bg-blue-50 border-2 border-blue-300 border-dashed',
          draggedCategoryIdRef?.current === category.id && 'opacity-30'
        )}
        style={{ paddingLeft: `${level * 24 + 12}px` }}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        {/* Expand/Collapse Icon */}
        {hasChildren ? (
          <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-600" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-600" />
            )}
          </div>
        ) : (
          <div className="w-5 h-5 flex items-center justify-center">
            <div className="w-1 h-1 rounded-full bg-gray-300" />
          </div>
        )}

        {/* Category Image/Icon/Color */}
        <div className="flex-shrink-0 relative">
          {category.image_url ? (
            <img
              src={category.image_url}
              alt={translatedTitle}
              className="w-10 h-10 rounded object-cover border border-gray-200"
              onError={(e) => {
                // Hide image and show fallback
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                const parent = target.parentElement
                if (parent) {
                  const fallback = parent.querySelector('.image-fallback')
                  if (fallback) {
                    // @ts-ignore
                    fallback.style.display = 'flex'
                  }
                }
              }}
            />
          ) : null}
          <div
            className={`image-fallback ${
              category.image_url ? 'hidden' : 'flex'
            } items-center justify-center`}
          >
            {category.color ? (
              <div
                className="w-10 h-10 rounded border border-gray-300 flex items-center justify-center relative"
                style={{ backgroundColor: category.color }}
                title={category.color}
              >
                <span className="text-xs text-gray-700 font-medium">
                  {translatedTitle.charAt(0).toUpperCase()}
                </span>
                <ImageOff className="absolute bottom-0 right-0 h-3 w-3 text-gray-500 bg-white rounded-full p-0.5" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded border border-gray-200 bg-gray-50 flex items-center justify-center">
                <ImageOff className="h-6 w-6 text-gray-400" />
              </div>
            )}
          </div>
        </div>

        {/* Category Title and Description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-medium text-sm text-gray-900">
              {translatedTitle}
            </div>
            <span className="text-xs text-gray-400 font-mono">
              #{category.id}
            </span>
            {category.color && (
              <div
                className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                style={{ backgroundColor: category.color }}
                title={category.color}
              />
            )}
            {productCount > 0 && (
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                {productCount} {t('product(s)', 'produkt(er)')}
              </span>
            )}
          </div>
          {category.description &&
            (() => {
              const descParts = category.description.split(' || ') || []
              const translatedDescription = t(
                descParts[0] || category.description,
                descParts[1] || category.description
              )
              return (
                <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                  {translatedDescription}
                </div>
              )
            })()}
        </div>

        {/* Actions */}
        <div
          className="flex-shrink-0 flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => onEdit(category)}
          >
            <Edit className="h-4 w-4 mr-1" />
            {t('Edit', 'Redigera')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <DotsHorizontalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {productCount > 0 && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    onMoveProducts(category, productCount)
                  }}
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  {t('Move Products', 'Flytta produkter')} ({productCount})
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => onDelete(category.id)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('Delete', 'Radera')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <CategoryItem
              key={child.category.id}
              node={child}
              level={level + 1}
              onEdit={onEdit}
              onDelete={onDelete}
              onMoveProducts={onMoveProducts}
              onCategoryDrop={onCategoryDrop}
              searchQuery={searchQuery}
              allCategories={allCategories}
              draggedCategoryIdRef={draggedCategoryIdRef}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export const CategoryTree = ({
  categories,
  onEdit,
  onDelete,
  onMoveProducts,
  onCategoryDrop,
  searchQuery,
  draggedCategoryIdRef,
}: CategoryTreeProps) => {
  const { t } = useLocalization()
  
  // Create a ref to track dragged category if not provided
  const internalDraggedCategoryIdRef = useRef<number | null>(null)
  const draggedIdRef = draggedCategoryIdRef || internalDraggedCategoryIdRef

  // Build tree structure
  const tree = useMemo(() => {
    // Create a map for quick lookup
    const categoryMap = new Map<number, CategoryNode>()
    const rootNodes: CategoryNode[] = []

    // Initialize all categories as nodes
    categories.forEach((category) => {
      categoryMap.set(category.id, {
        category,
        children: [],
      })
    })

    // Build parent-child relationships
    categories.forEach((category) => {
      const node = categoryMap.get(category.id)!
      if (category.parent_category_id) {
        const parent = categoryMap.get(category.parent_category_id)
        if (parent) {
          parent.children.push(node)
        } else {
          // Parent not found, treat as root
          rootNodes.push(node)
        }
      } else {
        // Root category
        rootNodes.push(node)
      }
    })

    // Note: We can't use t() here in useMemo, so we'll sort by English title
    // The display will still show translated titles
    const sortChildrenByEnglish = (node: CategoryNode) => {
      node.children.sort((a, b) => {
        const titleAParts = a.category.title?.split(' || ') || []
        const titleBParts = b.category.title?.split(' || ') || []
        const titleA = titleAParts[0] || a.category.title || ''
        const titleB = titleBParts[0] || b.category.title || ''
        return titleA.localeCompare(titleB)
      })
      node.children.forEach(sortChildrenByEnglish)
    }

    rootNodes.forEach(sortChildrenByEnglish)
    rootNodes.sort((a, b) => {
      const titleAParts = a.category.title?.split(' || ') || []
      const titleBParts = b.category.title?.split(' || ') || []
      const titleA = titleAParts[0] || a.category.title || ''
      const titleB = titleBParts[0] || b.category.title || ''
      return titleA.localeCompare(titleB)
    })

    return rootNodes
  }, [categories])

  if (tree.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        {t('No categories found', 'Inga kategorier hittades')}
      </div>
    )
  }

  return (
    <div className="py-2">
      {tree.map((node) => (
        <CategoryItem
          key={node.category.id}
          node={node}
          level={0}
          onEdit={onEdit}
          onDelete={onDelete}
          onMoveProducts={onMoveProducts}
          onCategoryDrop={onCategoryDrop}
          searchQuery={searchQuery}
          allCategories={categories}
          draggedCategoryIdRef={draggedIdRef}
        />
      ))}
    </div>
  )
}
